import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import CreatePostIcon from "assets/icons/create-post.svg";
import { CollectionFile } from "shared/types/files";
import { useGridMediaCard } from "hooks/useGridMediaCard";
import { entitlementsPermission } from "@/utils/entitlements-permission";
import { useSelector } from "react-redux";
import { getUser } from "@/selectors/commonSelectors";

const CreativePhotoEditorModal = lazy(() =>
  import("ui/photo-editor").then((m) => ({
    default: m.CreativePhotoEditorModal,
  }))
);

const CreativeVideoEditorModal = lazy(() =>
  import("ui/video-editor").then((m) => ({
    default: m.CreativeVideoEditorModal,
  }))
);

export interface GridMediaCardProps {
  asset: CollectionFile;
  className?: string;
  showCollectionTooltip?: boolean;
  order?: number;
  onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  hoverContent?: React.ReactNode; // Custom hover overlay content
  canSelect?: boolean; // Controls checkbox visibility (default: true)
  canFavorite?: boolean; // Controls favorite star visibility (default: true)
  selected?: boolean; // External selection state override
  selecting?: boolean; // External selecting mode override
  showActionButtons?: boolean; // Controls action buttons visibility (default: true)
  showCheckbox?: boolean; // Controls checkbox render (default: true)
  showFilename?: boolean; // Controls filename footer visibility (default: true)
  username?: string; // Username to display in upper right (for feed/hashtag media)
  isSourceMedia?: boolean; // Controls checkbox position (top-right when true for media picker)
}

// Moved outside component to avoid recreating on every render
const createCheckboxClasses = (
  selected: boolean,
  selecting: boolean,
  isSourceMedia?: boolean
) => {
  // Use cn utility for better class composition
  // Position: top-right for media picker (isSourceMedia), top-left for standalone library
  const positionClasses = isSourceMedia ? "top-2 right-2" : "top-2 left-2";
  const baseClasses = `absolute ${positionClasses} w-8 h-8 rounded-md flex items-center justify-center z-[3] transition-opacity duration-200`;

  // For source media (picker mode): always visible for clarity
  // For library mode: always visible when selected or selecting, hover-only otherwise
  if (isSourceMedia) {
    return `${baseClasses} opacity-100`;
  }

  // Always visible when selected or in selecting mode
  if (selected || selecting) {
    return `${baseClasses} opacity-100`;
  }

  // Hidden by default, visible on group hover
  return `${baseClasses} opacity-0 group-hover:opacity-100`;
};

const getAssetIconClass = (asset: CollectionFile) => {
  const isPdf = asset.meta?.mime === "application/pdf";

  if (isPdf) {
    return "fa-light fa-file-pdf";
  }

  if (asset.meta?.isVideo) {
    return "fa-light fa-video";
  }

  return "fa-light fa-image";
};

const getUsedTagTooltipText = (asset: CollectionFile) => {
  let fileType = "media";
  if (asset.meta?.isVideo) {
    fileType = "video";
  } else if (asset.meta?.isImage) {
    fileType = "image";
  } else if (asset.meta?.mime === "application/pdf") {
    fileType = "pdf";
  }

  return `This ${fileType} is being used in a draft or upcoming post.`;
};

/**
 * Smart filename truncation that preserves file extensions
 * Examples:
 *   "very-long-filename-here.jpg" -> "very-long-fil...jpg"
 *   "IMG_0183.jpg" -> "IMG_0183.jpg" (unchanged)
 *   "test" -> "test" (no extension)
 */
const truncateFilename = (filename: string, maxLength: number = 20): string => {
  if (filename.length <= maxLength) return filename;

  const lastDotIndex = filename.lastIndexOf(".");

  // No extension or extension is too long
  if (lastDotIndex === -1 || filename.length - lastDotIndex > 6) {
    return filename.substring(0, maxLength - 3) + "...";
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExt = filename.substring(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // -3 for "..."

  if (maxNameLength <= 0) return filename.substring(0, maxLength - 3) + "...";

  return nameWithoutExt.substring(0, maxNameLength) + "..." + extension;
};

const GridMediaCard = ({
  asset,
  className = "",
  showCollectionTooltip = false,
  order = 1,
  onCardClick,
  hoverContent,
  canSelect,
  canFavorite,
  selected: externalSelected,
  selecting: externalSelecting,
  showActionButtons = true,
  showCheckbox = true,
  showFilename = true,
  username,
  isSourceMedia = false,
}: GridMediaCardProps) => {
  // Handle undefined asset gracefully
  if (!asset) {
    return (
      <div
        className={cn(
          "relative aspect-square bg-neutral-200 rounded-lg overflow-hidden",
          className
        )}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <i className="fa-light fa-image text-neutral-400 text-2xl" />
        </div>
      </div>
    );
  }

  const {
    id,
    selected: hookSelected,
    selecting: hookSelecting,
    assetUrl,
    gifPreviewUrl,
    lowQualityVideoUrl,
    favorited,
    isVideo,
    assetIsUsed,
    isEditable,
    canSelect: hookCanSelect,
    canFavorite: hookCanFavorite,
    isManagedCollection,
    showPhotoEditor,
    photoEditorImageUrl,
    photoEditorAssetId,
    photoEditorAssetUrl,
    photoEditorIsReplacingOriginal,
    photoEditorProgress,
    photoEditorSaveButtonText,
    photoEditorMessage,
    photoEditorIsSaving,
    showVideoEditor,
    videoEditorVideoUrl,
    videoEditorAssetId,
    videoEditorAssetUrl,
    videoEditorIsReplacingOriginal,
    videoEditorProgress,
    videoEditorSaveButtonText,
    videoEditorMessage,
    videoEditorIsSaving,

    handleCardClick,
    handleFavoriteClick,
    handleCreatePost,
    handleCopy,
    handleMove,
    handleDelete,
    handleViewDetails,
    handleEdit,

    handlePhotoEditorClose,
    handlePhotoEditorSave,
    handleVideoEditorClose,
    handleVideoEditorSave,
  } = useGridMediaCard(asset, onCardClick, {
    canSelect,
    canFavorite,
  });

  // Use external selection state if provided, otherwise use hook values
  const selected =
    externalSelected !== undefined ? externalSelected : hookSelected;
  const selecting =
    externalSelecting !== undefined ? externalSelecting : hookSelecting;

  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [videoPreviewLoaded, setVideoPreviewLoaded] = useState(false);
  const [gifPreviewLoaded, setGifPreviewLoaded] = useState(false);
  const [videoPreviewError, setVideoPreviewError] = useState(false);
  const [gifPreviewError, setGifPreviewError] = useState(false);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const user = useSelector(getUser);
  const gifPreviewSrc =
    gifPreviewUrl || assetUrl.replace("thumbnail.jpg", "gif.gif");
  const canUseInlineVideoPreview = Boolean(
    lowQualityVideoUrl && !videoPreviewError
  );
  const canUseGifPreview = Boolean(
    !canUseInlineVideoPreview && gifPreviewUrl && !gifPreviewError
  );
  const showInlineVideoPreview =
    isVideo &&
    showVideoPreview &&
    canUseInlineVideoPreview &&
    videoPreviewLoaded;
  const showAnimatedGifPreview =
    isVideo &&
    showVideoPreview &&
    canUseGifPreview &&
    gifPreviewLoaded &&
    !gifPreviewError;
  const showThumbnailImage =
    !isVideo ||
    !showVideoPreview ||
    (!showInlineVideoPreview && !showAnimatedGifPreview);

  const tooltipPlacement =
    order % 5 === 4 || order % 5 === 0 ? "left" : "right";

  // Memoize checkbox classes to avoid recalculating on every render
  const checkboxClasses = useMemo(
    () => createCheckboxClasses(selected, selecting, isSourceMedia),
    [selected, selecting, isSourceMedia]
  );

  useEffect(() => {
    setVideoPreviewLoaded(false);
    setVideoPreviewError(false);
  }, [lowQualityVideoUrl]);

  useEffect(() => {
    setGifPreviewLoaded(false);
    setGifPreviewError(false);
  }, [gifPreviewSrc]);

  // Preload GIF preview only when we need the legacy fallback.
  useEffect(() => {
    if (isVideo && canUseGifPreview && !gifPreviewLoaded && !gifPreviewError) {
      const img = new Image();
      img.addEventListener("load", () => setGifPreviewLoaded(true));
      img.addEventListener("error", () => setGifPreviewError(true));
      img.src = gifPreviewSrc;
    }
  }, [
    isVideo,
    canUseGifPreview,
    gifPreviewLoaded,
    gifPreviewError,
    gifPreviewSrc,
  ]);

  useEffect(() => {
    const previewVideo = videoPreviewRef.current;
    if (!previewVideo) {
      return;
    }

    if (showVideoPreview && canUseInlineVideoPreview) {
      previewVideo.play().catch(() => {
        // Playback interrupted (e.g. quick hover-out); ignore.
      });
    } else {
      previewVideo.pause();
      previewVideo.currentTime = 0;
    }
  }, [showVideoPreview, canUseInlineVideoPreview]);

  return (
    <div
      className={cn(
        "relative",
        selected && "bg-blue-50 rounded-lg ring-4 ring-blue-500",
        className
      )}
      data-testid="assetContainer"
    >
      <div className="relative w-full aspect-square">
        <div className="absolute inset-0 w-full h-full group">
          <div
            className={cn(
              "relative w-full h-full cursor-pointer rounded-lg overflow-hidden flex flex-col",
              "transition-shadow duration-200",
              selected ? "" : "border border-neutral-100 hover:shadow-lg"
            )}
            onClick={(e) => handleCardClick(e)}
            onMouseEnter={() => isVideo && setShowVideoPreview(true)}
            onMouseLeave={() => isVideo && setShowVideoPreview(false)}
          >
            <div className="bg-neutral-100 w-full flex-1 relative flex items-center justify-center overflow-hidden">
              {/* Blurred backdrop preserves the square grid while showing the full crop in front. */}
              <img
                aria-hidden="true"
                className={`absolute inset-0 z-[0] h-full w-full scale-125 object-cover object-center blur-sm transition-opacity duration-300 ease-in-out ${showThumbnailImage ? "opacity-100" : "opacity-0"}`}
                style={{ WebkitUserDrag: "none" } as React.CSSProperties}
                src={assetUrl}
                alt=""
              />
              <div className="absolute inset-0 bg-black/5 z-[1]" />

              {/* Thumbnail Image - Always rendered */}
              <img
                className={`absolute inset-0 h-full w-full object-contain object-center max-w-full max-h-full select-none transition-opacity duration-300 ease-in-out ${showThumbnailImage ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                style={{ WebkitUserDrag: "none" } as React.CSSProperties}
                src={assetUrl}
                alt={`Media asset ${id}`}
                data-testid="gridMediaCardImage"
              />

              {/* Video Preview - Always rendered for videos */}
              {isVideo && (
                <>
                  {lowQualityVideoUrl && (
                    <video
                      ref={videoPreviewRef}
                      className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-300 ease-in-out ${showInlineVideoPreview ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                      src={lowQualityVideoUrl}
                      muted
                      loop
                      playsInline
                      preload="none"
                      onLoadedData={() => setVideoPreviewLoaded(true)}
                      onError={() => setVideoPreviewError(true)}
                      data-testid="gridMediaCardVideoPreview"
                    />
                  )}
                  {canUseGifPreview && (
                    <>
                      <img
                        aria-hidden="true"
                        className={`absolute inset-0 z-[0] h-full w-full scale-110 object-cover object-center blur-2xl transition-opacity duration-300 ease-in-out ${showAnimatedGifPreview ? "opacity-100" : "opacity-0"}`}
                        style={
                          { WebkitUserDrag: "none" } as React.CSSProperties
                        }
                        src={gifPreviewSrc}
                        alt=""
                      />
                      <img
                        className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-300 ease-in-out ${showAnimatedGifPreview ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                        style={
                          { WebkitUserDrag: "none" } as React.CSSProperties
                        }
                        src={gifPreviewSrc}
                        alt="Video preview"
                        data-testid="gridMediaCardGifPreview"
                      />
                    </>
                  )}
                </>
              )}

              {/* Play Button for Videos */}
              {isVideo &&
                (!showVideoPreview ||
                  (!showInlineVideoPreview && !showAnimatedGifPreview)) && (
                  <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[3]"
                    style={{
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                      borderWidth: "13px 0 13px 20px",
                      borderColor:
                        "transparent transparent transparent #ffffff",
                    }}
                  />
                )}

              {/* Custom Hover Overlay */}
              {hoverContent && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/60 z-[2] rounded-lg overflow-hidden">
                  {hoverContent}
                </div>
              )}

              {/* Used flag */}
              {assetIsUsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="absolute left-2 bottom-2 rounded-full bg-black/40 flex items-center justify-center text-white text-base font-medium px-2 py-2 z-[2]">
                      <i className="fa-regular fa-check-to-slot"></i>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[140px] text-xs text-center"
                  >
                    {getUsedTagTooltipText(asset)}
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Username badge for feed/hashtag media */}
              {username && (
                <div className="absolute right-2 top-2 rounded-md bg-black/50 backdrop-blur-sm flex items-center text-white text-xs font-medium px-2 py-1 z-[2] max-w-[calc(100%-16px)] overflow-hidden">
                  <span className="truncate">@{username}</span>
                </div>
              )}
            </div>

            {showFilename && (
              <div
                className={cn(
                  "flex items-center justify-between px-2 py-2 gap-2 w-full border-t border-neutral-100",
                  selected ? "bg-blue-50" : "bg-white"
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`${getAssetIconClass(asset)} text-neutral-700 text-sm`}
                    />
                  </div>
                  {(asset?.name || "Untitled").length > 20 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="font-inter text-xs font-normal text-neutral-900 whitespace-nowrap overflow-hidden text-ellipsis min-w-0 cursor-default">
                          {truncateFilename(asset?.name || "Untitled")}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[300px] break-all"
                      >
                        {asset?.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div className="font-inter text-xs font-normal text-neutral-900 whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                      {asset?.name || "Untitled"}
                    </div>
                  )}
                </div>

                {hookCanFavorite && (
                  <div
                    className="w-5 h-5 flex items-center justify-center cursor-pointer opacity-80 hover:opacity-100 flex-shrink-0"
                    onClick={handleFavoriteClick}
                    data-testid="favourite-button"
                  >
                    <i
                      className={cn(
                        "text-yellow-500",
                        favorited ? "fa-solid fa-star" : "fa-regular fa-star"
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Overlay Controls */}
          {hookCanSelect && showCheckbox && (
            <div className={checkboxClasses}>
              <Checkbox
                data-testid="select-button"
                checked={selected}
                onCheckedChange={() => handleCardClick()}
                className="bg-white/90 backdrop-blur-sm shadow-sm data-[state=checked]:bg-blue-500 p-0 w-5 h-5"
              />
            </div>
          )}

          {/* Action Buttons */}
          {showActionButtons && !selected && !selecting && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-2 z-[3] transition-opacity duration-200">
              <button
                className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-600 hover:scale-105 p-0 shadow-md transition-all duration-150"
                onClick={handleCreatePost}
              >
                <div className="w-4 h-4 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
                  <CreatePostIcon />
                </div>
              </button>

              {/* Action Menu - shadcn DropdownMenu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-8 h-8 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 hover:border-neutral-300 hover:shadow-md flex items-center justify-center p-0 transition-all duration-150"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fa-solid fa-ellipsis-vertical text-neutral-500 text-sm hover:text-neutral-700" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-[160px]">
                  {entitlementsPermission.hasEditorLibrary(user) &&
                    isEditable && (
                      <DropdownMenuItem onSelect={() => handleEdit()}>
                        <i className="fa-regular fa-pen-to-square text-sm text-neutral-400 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}

                  <DropdownMenuItem
                    onSelect={() => handleMove()}
                    disabled={isManagedCollection}
                  >
                    <i className="fa-regular fa-arrows-up-down-left-right text-sm text-neutral-400 mr-2" />
                    Move
                  </DropdownMenuItem>

                  <DropdownMenuItem onSelect={() => handleCopy()}>
                    <i className="fa-regular fa-clone text-sm text-neutral-400 mr-2" />
                    Copy
                  </DropdownMenuItem>

                  <DropdownMenuItem onSelect={() => handleViewDetails()}>
                    <i className="fa-regular fa-square-info text-sm text-neutral-400 mr-2" />
                    Details
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onSelect={() => handleDelete()}
                    disabled={isManagedCollection}
                    className="text-red-600 focus:text-red-600"
                  >
                    <i className="fa-regular fa-trash-can text-sm mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Selection Border */}
          {selected && (
            <div className="absolute -inset-0.5 border-2 border-blue-500 rounded-xl pointer-events-none" />
          )}
        </div>
      </div>

      {/* Collection Tooltip */}
      {showCollectionTooltip && (
        <div
          className={cn(
            "absolute w-48 bg-neutral-900 text-white p-2 rounded opacity-100 z-10",
            tooltipPlacement === "left" ? "right-full" : "left-full"
          )}
        >
          Please select a Collection
        </div>
      )}

      {/* Photo Editor Modal */}
      {showPhotoEditor && (
        <Suspense fallback={null}>
          <CreativePhotoEditorModal
            show={showPhotoEditor}
            image={photoEditorImageUrl}
            assetId={photoEditorAssetId}
            assetUrl={photoEditorAssetUrl}
            isReplacingOriginal={photoEditorIsReplacingOriginal}
            onClose={handlePhotoEditorClose}
            onSave={handlePhotoEditorSave}
            progress={photoEditorProgress}
            saveButtonText={photoEditorSaveButtonText}
            message={photoEditorMessage}
            isSaving={photoEditorIsSaving}
          />
        </Suspense>
      )}

      {/* Video Editor Modal */}
      {showVideoEditor && (
        <Suspense fallback={null}>
          <CreativeVideoEditorModal
            show={showVideoEditor}
            video={videoEditorVideoUrl}
            assetId={videoEditorAssetId}
            assetUrl={videoEditorAssetUrl}
            isReplacingOriginal={videoEditorIsReplacingOriginal}
            onClose={handleVideoEditorClose}
            onSave={handleVideoEditorSave}
            progress={videoEditorProgress}
            saveButtonText={videoEditorSaveButtonText}
            message={videoEditorMessage}
            isSaving={videoEditorIsSaving}
          />
        </Suspense>
      )}
    </div>
  );
};

export default GridMediaCard;
